import { useEffect } from 'react';

import ArrowSvg from '../../assets/images/layout/arrow.svg';
import { useLayoutBottomLeftContext } from '../layout/LayoutBottomLeftContext';
import { cn } from '../../utils/cn';
import { useLenis } from '../../utils/LenisContext';
import { usePrefersReducedMotion } from '../../utils/usePrefersReducedMotion';

export const ScrollIncentive = () => {
  const { lenis } = useLenis();
  const prefersReducedMotion = usePrefersReducedMotion();
  const {
    isScrollIncentiveEnabled,
    isScrollIncentiveVisible,
    setScrollIncentiveVisible,
  } = useLayoutBottomLeftContext();

  useEffect(() => {
    if (!isScrollIncentiveEnabled) {
      return;
    }

    const handleScroll = () => {
      if (window.scrollY > 200) {
        setScrollIncentiveVisible(false);
      }
    };

    const timeout = window.setTimeout(() => {
      if (window.scrollY <= 200) {
        setScrollIncentiveVisible(true);
      }
    }, 3000);

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isScrollIncentiveEnabled, setScrollIncentiveVisible]);

  if (!isScrollIncentiveEnabled) {
    return null;
  }

  const scrollToTarget = () => {
    const target = document.querySelector<HTMLElement>(
      '[data-scroll-incentive-target]',
    );
    const scrollOffset = Number(target?.dataset.scrollOffset) || 0;
    const scrollTo = (top: number) => {
      if (lenis) {
        lenis.scrollTo(top, { immediate: prefersReducedMotion });
        return;
      }

      window.scrollTo({
        top,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    };

    if (target) {
      scrollTo(
        Math.max(
          0,
          target.getBoundingClientRect().top + window.scrollY - scrollOffset,
        ),
      );
      return;
    }

    scrollTo(window.scrollY + window.innerHeight);
  };

  return (
    <div className="pointer-events-none fixed inset-0 z-30 mx-auto flex h-full w-full max-w-[1500px] short-viewport:w-[calc(100%-36px)] items-end justify-start pb-[40px] pl-[25px] mix-blend-exclusion [@media(width>=768px)_and_(height>700px)]:pb-[65px] [@media(width>=768px)_and_(height>700px)]:pl-[45px] short-viewport:pl-[7px] short-viewport:pb-[31px]">
      <button
        type="button"
        aria-hidden={!isScrollIncentiveVisible}
        aria-label="Scroll down"
        tabIndex={isScrollIncentiveVisible ? 0 : -1}
        onClick={scrollToTarget}
        className={cn(
          'text-foreground flex h-12 w-12 cursor-pointer items-center justify-center border-0 bg-transparent p-0 transition-opacity duration-300',
          isScrollIncentiveVisible
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0',
        )}
      >
        <span className="motion-safe:animate-bounce">
          <span className="block w-[50px] rotate-90 [&_svg]:h-auto [&_svg]:w-full [&_svg]:fill-none [&_svg]:stroke-current [&_svg_*]:stroke-current">
            <ArrowSvg aria-hidden="true" focusable="false" />
          </span>
        </span>
      </button>
    </div>
  );
};
